
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useMutation as useConvexMutation, useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface CreateLevelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateLevelDialog = ({ open, onOpenChange }: CreateLevelDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Get current user to access role
  const { user } = useCurrentUser();
  const createLevelMutation = useConvexMutation(api.levels.create.level);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    if (!user?.roles?.[0]?._id) {
      toast({
        title: "Error",
        description: "User role is required to create levels.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      await createLevelMutation({
        name: name.trim(),
        description: description.trim(),
        template: false,
      });
      
      toast({
        title: "Level created",
        description: "Your new level has been created successfully.",
      });
      
      onOpenChange(false);
      setName("");
      setDescription("");
    } catch (error) {
      console.error('Error creating level:', error);
      toast({
        title: "Error",
        description: "Failed to create level. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setName("");
    setDescription("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Level</DialogTitle>
            <DialogDescription>
              Create a new level to organize devices with custom rules and restrictions. New levels are created as drafts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter level name"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter level description (optional)"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!name.trim() || isLoading}
            >
              {isLoading ? "Creating..." : "Create Level"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
